import React, { useEffect, useState } from "react";
import "./AddCar.css";
import ApiManager from "../../ApiManager/ApiManager";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

const AddCar = ({}) => {
  const navigate = useNavigate();
  const { withLoading, isLoading } = useComponentLoading();
  const [permits, setPermits] = useState([{ permitType: "", expiry: "" }]);

  const [driverData, setDriverData] = useState([]);
  const [carTypeData, setCarTypeData] = useState([]);

  const convertTimeToNumber = (inputTime) => {
    if (inputTime) {
      let [time, modifier] = inputTime.split(" ");
      let [hours, minutes] = time.split(":");

      hours = Number(hours);
      minutes = Number(minutes);

      if (modifier?.toLowerCase() === "pm" && hours !== 12) {
        hours += 12;
      } else if (modifier?.toLowerCase() === "am" && hours === 12) {
        hours = 0;
      }

      const formattedTime = `${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}:00`;
      return formattedTime;
    }
    return null;
  };

  const convertDateToString = (inputDate) => {
    if (!inputDate) return null;

    const parts = inputDate.split("-");

    if (parts.length !== 3) return null;
    if (parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    } else {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  };

  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        const params = new URLSearchParams({
          page_size: 1000,
          page: 1,
        });
        const response = await ApiManager.getAllDrivers(params);
        if (!response.ok) throw new Error("Network response was not ok");
        const json = await response.json();
        console.log("res", json);
        setDriverData(json);
      } catch (error) {
        console.log("error", error);
      }
    };

    const fetchCarTypeData = async () => {
      try {
        const params = new URLSearchParams({
          page_size: 1000,
          page: 1,
        });
        const response = await ApiManager.getCarType(params);
        if (!response.ok) throw new Error("Network response was not ok");
        const json = await response.json();
        console.log("res", json);
        setCarTypeData(json);
      } catch (error) {
        console.log("error", error);
      }
    };

    fetchDriverData();
    fetchCarTypeData();
  }, []);

  const handlePermitChange = (index, field, value) => {
    const updatedPermits = [...permits];
    updatedPermits[index][field] = value;
    setPermits(updatedPermits);
  };

  const addPermit = () => {
    setPermits([...permits, { permitType: "", expiry: "" }]);
  };

  const removePermit = (index) => {
    const updatedPermits = permits.filter((_, i) => i !== index);
    setPermits(updatedPermits);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get form data
    const formData = new FormData(e.target);
    const vehicleData = Object.fromEntries(formData.entries());

    // Convert vehicleData to the correct structure
    const payload = {
      vehicleNo: vehicleData.vehicleNo,
      vehicleName: vehicleData.vehicleName,
      modelName: vehicleData.modelName,
      color: vehicleData.color,
      fuelType: vehicleData.fuelType,
      assignedDriverId: Number(vehicleData.assignedDriverId),
      capacity: Number(vehicleData.capacity),
      type: Number(vehicleData.type),
      permits: permits.map((permit) => ({
        permitType: permit.permitType,
        expiry: convertDateToString(permit.expiry),
      })),
      registrationInfo: {
        ownerName: vehicleData.ownerName,
        registrationDate: convertDateToString(vehicleData.registrationDate),
        insuranceInfo: {
          insuranceCompany: vehicleData.insuranceCompany,
          insurancePolicyNo: vehicleData.insurancePolicyNo,
          insuranceIssueDate: convertDateToString(vehicleData.insuranceIssueDate),
          insuranceDueDate: convertDateToString(vehicleData.insuranceDueDate),
        },
        fitnessInfo: {
          fitnessNumber: vehicleData.fitnessNumber,
          fitnessExpiryDate: convertDateToString(vehicleData.fitnessExpiryDate),
        },
        rtoInfo: {
          rtoAddress: vehicleData.rtoAddress,
          rtoExpiry: convertDateToString(vehicleData.rtoExpiry),
        },
      },
    };

    try {
      await withLoading('addCar', async (onProgress) => {
        onProgress(20, 'Preparing car data...');
        console.log("Payload:", payload);
        
        onProgress(50, 'Creating car...');
        const response = await ApiManager.addCar(payload);
        console.log("Response:", response);
        
        if (!response.ok) throw new Error(await response.text());
        
        onProgress(80, 'Car created successfully');
        navigate('/login/cars');
        onProgress(100, 'Redirecting...');
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(error.message || "Failed to add car");
    }
  };

  return (
    <div className="addCar">
      <div>
        <h3>Add Car</h3>
      </div>
      <form onSubmit={handleSubmit}>
        {/* Vehicle Details Section */}
        <fieldset className="fieldset">
          <legend>Vehicle Details</legend>
          <div className="row">
            <div className="formGroup">
              <label>Vehicle No *</label>
              <input
                type="text"
                name="vehicleNo"
                placeholder="Enter vehicle number"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Vehicle Name *</label>
              <input
                type="text"
                name="vehicleName"
                placeholder="Enter vehicle name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Model Name *</label>
              <input
                type="text"
                name="modelName"
                placeholder="Enter model name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Color *</label>
              <input
                type="text"
                name="color"
                placeholder="Enter color"
                required
                className="input"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Fuel Type *</label>
              <select name="fuelType" required className="input">
                <option value="">Select Fuel Type</option>
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
                <option value="Electric">Electric</option>
                <option value="CNG">CNG</option>
              </select>
            </div>
            <div className="formGroup">
              <label>Capacity *</label>
              <input
                type="number"
                name="capacity"
                placeholder="Enter seating capacity"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Assigned Driver *</label>
              <Select
                className="input custom-select"
                name="assignedDriverId"
                options={(driverData || []).map((driver) => ({
                  value: driver.id,
                  label: driver.firstName,
                }))}
                placeholder="Select Driver"
                isClearable
                isSearchable
                required
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: "none",
                    boxShadow: "none",
                    fontSize: "1em",
                    height: "16px",
                    minHeight: "16px",
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: "0px 8px",
                    height: "16px",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "16px",
                  }),
                }}
              />
            </div>
            <div className="formGroup">
              <label>Type *</label>
              <select name="type" required className="input">
                <option value="">Select Type</option>
                {(carTypeData || []).map((carType) => (
                  <option key={carType.id} value={carType.id}>
                    {carType.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </fieldset>

        {/* Permits Section */}
        <fieldset className="fieldset">
          <legend>Permits</legend>
          {permits.map((permit, index) => (
            <div key={index} className="permitRow">
              <div className="formGroup">
                <label>Permit Type *</label>
                <input
                  type="text"
                  value={permit.permitType}
                  onChange={(e) =>
                    handlePermitChange(index, "permitType", e.target.value)
                  }
                  placeholder="Enter permit type"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Expiry Date *</label>
                <input
                  type="date"
                  value={permit.expiry}
                  onChange={(e) =>
                    handlePermitChange(index, "expiry", e.target.value)
                  }
                  required
                  className="input"
                />
              </div>
              <button
                type="button"
                onClick={() => removePermit(index)}
                className="remove-button"
              >
                x
              </button>
            </div>
          ))}
          <button type="button" onClick={addPermit} className="custom-button">
            + Add Permit
          </button>
        </fieldset>

        {/* Registration Information Section */}
        <fieldset className="fieldset">
          <legend>Registration Information</legend>
          <div className="row">
            <div className="formGroup">
              <label>Owner Name *</label>
              <input
                type="text"
                name="ownerName"
                placeholder="Enter owner name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Registration Date *</label>
              <input
                type="date"
                name="registrationDate"
                required
                className="input"
              />
            </div>
          </div>
          <fieldset className="fieldset">
            <legend>Insurance Information</legend>
            <div className="row">
              <div className="formGroup">
                <label>Insurance Company *</label>
                <input
                  type="text"
                  name="insuranceCompany"
                  placeholder="Enter insurance company"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Policy No *</label>
                <input
                  type="text"
                  name="insurancePolicyNo"
                  placeholder="Enter policy number"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Issue Date *</label>
                <input
                  type="date"
                  name="insuranceIssueDate"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Due Date *</label>
                <input
                  type="date"
                  name="insuranceDueDate"
                  required
                  className="input"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="fieldset">
            <legend>Fitness Information</legend>
            <div className="row">
              <div className="formGroup">
                <label>Fitness Number *</label>
                <input
                  type="text"
                  name="fitnessNumber"
                  placeholder="Enter fitness number"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Fitness Expiry Date *</label>
                <input
                  type="date"
                  name="fitnessExpiryDate"
                  required
                  className="input"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="fieldset">
            <legend>RTO Information</legend>
            <div className="row">
              <div className="formGroup">
                <label>RTO Address *</label>
                <input
                  type="text"
                  name="rtoAddress"
                  placeholder="Enter RTO address"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>RTO Expiry Date *</label>
                <input
                  type="date"
                  name="rtoExpiry"
                  required
                  className="input"
                />
              </div>
            </div>
          </fieldset>
        </fieldset>

        {/* Submit Button */}
        <button 
          type="submit" 
          className="submitButton"
          disabled={isLoading('addCar')}
        >
          {isLoading('addCar') ? (
            <LoadingSpinner 
              size="xs"
              inline={true}
            />
          ) : (
            'Submit'
          )}
        </button>
      </form>
    </div>
  );
};

export default AddCar;
